import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import SkillEditorForm, {
  SkillEditorMode,
  skillValidationSchema,
} from "~/components/assistant/SkillEditorForm";
import type { SkillFormData, SkillFormErrors } from "~/components/assistant/SkillEditorForm";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { DEFAULT_SKILL_CONTENT } from "~/services/skills";

const MODAL_TITLES: Record<SkillEditorMode, string> = {
  [SkillEditorMode.CREATE]: "Add New Skill",
  [SkillEditorMode.EDIT]: "Edit Skill",
};

interface SkillEditorModalProps {
  errors?: SkillFormErrors;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: SkillFormData) => void | Promise<void>;
  open: boolean;
  initialData?: Partial<SkillFormData>;
  mode?: SkillEditorMode;
}

const SkillEditorModal = ({
  errors = {},
  loading = false,
  onClose,
  onSubmit,
  open,
  initialData,
  mode = SkillEditorMode.CREATE,
}: SkillEditorModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<SkillFormData>({
    resolver: yupResolver(skillValidationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      content: DEFAULT_SKILL_CONTENT,
    },
  });

  // Sync form when initialData prop changes (e.g., after async fetch) or when dialog opens
  useEffect(() => {
    if (open) {
      reset({
        name: initialData?.name ?? "",
        description: initialData?.description ?? "",
        content: initialData?.content ?? DEFAULT_SKILL_CONTENT,
      });
    }
  }, [open, initialData, reset]);

  const handleFormSubmit = (data: SkillFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="sm:max-w-[900px] sm:w-[calc(100%_-_64px)] p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>{MODAL_TITLES[mode]}</DialogTitle>
        </DialogHeader>

        <SkillEditorForm control={control} errors={errors} loading={loading} mode={mode} />

        <DialogFooter className="px-6 pb-4 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={loading} size="sm">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            disabled={!isValid || loading}
            loading={loading}
            size="sm"
            className="min-w-[72px]"
          >
            {mode === SkillEditorMode.EDIT ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkillEditorModal;
